export const ANIMATION_CLASSNAME = 'mod-anim' as const;

export const BILLING_METHODS = {
  CREDIT_CARD: 'credit_card',
  FREE: 'free',
  PAYPAL: 'paypal',
  APPLEPAY: 'applepay',
  VENMO: 'venmo',
  GOOGLEPAY: 'googlepay',
  SAVED_PAYMENT: 'saved_payment',
  TRUEMED: 'truemed',
} as const;

// Methods whose submit handler replays a nonce captured earlier rather than re-invoking its SDK,
// and whose billing radio is gated on still holding it — so losing the nonce has to take the
// method too. PayPal and Venmo re-authorize on submit, so they are deliberately absent (BKO-5365).
export const WALLET_METHODS: string[] = [BILLING_METHODS.APPLEPAY, BILLING_METHODS.GOOGLEPAY];

export const BILLING_ADDRESS = {
  SHIPPING: 0,
  CUSTOM: 1,
  SAVED: 2,
  SAVED_CUSTOM: 3,
} as const;

// Mirrors custom_address_sources in store/lib/payment_method.py; anything else, unset included,
// verifies the SHIPPING address. Not constantsv2's CUSTOM_ADDRESS_SOURCES, which omits SAVED.
export const CUSTOM_BILLING_ADDRESS_SOURCES: number[] = [
  BILLING_ADDRESS.CUSTOM,
  BILLING_ADDRESS.SAVED,
  BILLING_ADDRESS.SAVED_CUSTOM,
];

// The methods BillingAddress renders a billing-address block for; every other method gets
// display:none, so there is no field to send that customer back to. Read by both.
export const BILLING_ADDRESS_FORM_METHODS: string[] = [
  BILLING_METHODS.CREDIT_CARD,
  BILLING_METHODS.SAVED_PAYMENT,
];

export const DEBOUNCE_TIME = 750;

export const CC = {
  fields: ['number', 'expirationDate', 'cvv'] as const,
  number: '#js-card-number',
  expirationDate: '#js-expiration-date',
  cvv: '#js-cvv',
} as const;

export const ELEMENTS = {
  addressSection: 'js-address',
  verification: 'js-verification',
  accept: 'js-accept',
  billing: 'js-billing',
  ccCVV: 'js-cvv',
  ccExp: 'js-expiration-date',
  ccForm: 'js-card-form',
  ccNum: 'js-card-number',
  review: 'js-review',
  root: 'js-spc',
  shipping: 'js-shipping',
  shippingMethod: 'js-shipping-method',
  email: 'js-shipping-email',
  phone: 'js-shipping-phone',
  firstName: 'js-shipping-firstname',
  lastName: 'js-shipping-lastname',
  company: 'js-shipping-company',
  address: 'js-shipping-address',
  city: 'js-shipping-city',
  state: 'js-shipping-state',
  postalCode: 'js-shipping-zip',
  country: 'js-shipping-country',
  address2: 'js-shipping-address2',
} as const;

// {paymentMethod}, {wallet} and {amount} are filled by buildPastDueRetryModal, except SAVE_AND_PAY's
// {amount}, which its callers fill; **text** renders bold.
export const PAST_DUE_RETRY_RESULT = {
  PROCESSING: 'Processing payment...',
  NOTICE:
    'You have a past due payment. Updating your payment method will automatically trigger a charge for the outstanding balance on your membership.',
  SAVE_AND_PAY: 'Save & Pay {amount}',
  charged: {
    title: 'Payment complete',
    body: 'We saved your {paymentMethod} and used it to pay your {amount} past\u2011due balance.',
    button: 'Done',
  },
  declined: {
    title: 'Payment declined',
    body: 'We saved your {paymentMethod}, but your bank declined the {amount} past\u2011due payment. Please add a different card to complete the payment.',
    button: 'Close',
  },
  error: {
    title: 'Payment not completed',
    body: "Something went wrong on our end. We saved your {paymentMethod}, but couldn't process the {amount} past\u2011due payment. To try again, select **Edit** on this card, then **Save & Pay**.",
    walletBody:
      "We couldn't process your {amount} past\u2011due payment. {wallet} is saved to your account, but to pay this balance, you'll need to add a new payment method.",
    button: 'Close',
  },
} as const;

export const ERROR_MESSAGES = {
  // Not "shipping address": Labs reads this bucket too and collects no shipping address, billing to
  // a custom one instead. The step/element on each caller says which form is meant.
  ADDRESS:
    'There was a problem with the address you entered. Please double-check it and try again.',
  AVS_BILLING:
    "The billing address you entered doesn't match the one on file with your card issuer. Please double-check and update your billing address before trying again.",
  AVS_SWITCH_METHOD:
    "Your card issuer couldn't verify the billing address for this payment method. Please choose a different payment method to continue.",
  // No "enter a separate billing address" suggestion: this reaches wallet customers too, and the
  // billing-address block is display:none for them (BILLING_ADDRESS_FORM_METHODS).
  AVS_SHIPPING:
    "Your card issuer couldn't verify the billing address on file for this card. Since you're using your shipping address for billing, please double-check it below.",
  DIGITAL_WALLET_ADDRESS: 'Please enter your shipping address to complete your order.',
  EMAIL: 'There was a problem with processing your email address. Please try again.',
  BILLING: 'Please return to the billing section to try again.',
  GENERIC_DECLINE:
    'We were unable to process your payment. Please use a different payment method or try again later.',
  EMPTY_CART: 'Cannot process your request.',
  SHIP_METHOD: 'There was a problem processing your shipping information. Please try again.',
  DEFAULT: 'Something went wrong.',
  BILLING_PROCESSING:
    'Something went wrong. Please try a different payment method or try again later.',
  NO_SHIPPING_METHODS_FOUND:
    'No shipping methods are available for the specified address. Please verify the address details and resubmit your request.',
  BANK_DECLINED:
    'Your bank has declined this transaction. Please try another payment method or contact your bank to resolve the issue.',
  EXPIRED_CARD:
    'This payment could not be completed. Please double-check your card number and expiration date.',
  CARD_NOT_ACTIVATED:
    'This card may not be activated yet. Please activate your card with your bank, or use a different one to complete your purchase.',
  NO_ACCOUNT:
    'The card number you entered could not be found. Please check for a typo and try again.',
  PROCESSOR_DECLINED:
    'We were unable to process your payment. Please use a different payment method or try again later.',
  CVV_DECLINED:
    "We couldn't verify your card's security code. Please double-check it and try again.",
  PAYPAL:
    'There was an issue with your PayPal authorization. Please log in to PayPal to resolve it, or choose another payment method.',
  CHECKOUT_SESSION_EXPIRED:
    'Your checkout session has expired. Please reload the page to continue.',
  PAYMENT_LOAD_FAILED: "We couldn't load the payment form. Please reload the page and try again.",
  PAYPAL_NETWORK: 'Unable to connect to PayPal. Please try again in a moment.',
  PAYPAL_ACCOUNT_UNUSABLE:
    'Your PayPal account cannot be used for this transaction. Please try a different payment method (credit card) or contact support.',
  // BKO-5365: the single-use nonce was consumed by the failed attempt, so re-entry is the only
  // recovery — "try again later" would be actively wrong advice here.
  REENTER_PAYMENT:
    'Your payment information can no longer be used. Please re-enter your payment details to try again.',
  // The states/labels fetch failed; the address was never rejected, so this must not reuse ADDRESS.
  // Names no control: most regions ship to one country and render it as static text (BKO-5361).
  ADDRESS_LOAD_FAILED: 'We ran into an unexpected issue. Please try again.',
} as const;

export const FETCH_TIMEOUT = 25000;

// CartApi timeout: 10s (P95 in production is ~1.1s, but set higher for CI parallel test runs
// where underprovisioned Magento resources can cause latency spikes)
export const CART_API_TIMEOUT = 20000;

// A slow reconcile is worse than none: it runs behind the cart's pointer-blocking scrim, against
// a server that just failed this mutation, so the reload prompt must not wait out the 20s above.
export const CART_RECONCILE_TIMEOUT = 5000;

export const GIFT_WRAP_OPTIONS = {
  GIFT: 'gift',
  PRIVACY: 'privacy',
} as const;

// Clinic owns the real expiry window, and no service publishes a per-order count, so both
// deadline sentences quote this one rather than a value off the wire (BKO-5546).
export const LAB_EXPIRATION_MONTHS = 12;

export const KEY_CODE = {
  ENTER: 13,
  SPACE: 32,
} as const;

export const KEY_NAME = {
  ENTER: 'Enter',
  SPACE: ' ',
} as const;

export const MAGENTO_SHIPPING_ADDRESS_ERROR_KEYWORDS = {
  ADDRESS: 'address',
  POSTAL_CODE: 'postal code',
} as const;

export const MAGENTO_AVS_ERROR_KEYWORDS = {
  AVS: 'avs',
  BILLING_ADDRESS: 'billing address',
} as const;

export const MAGENTO_SHIPPING_ERROR_KEYWORDS = {
  SHIPPING_METHOD: 'shipping method',
} as const;

export const MAGENTO_GENERIC_BILLING_ERROR_KEYWORDS = {
  DECLINE: 'declined',
  PAYMENT: 'payment',
} as const;

export const MAGENTO_INSUFFICIENT_FUNDS_KEYWORDS = {
  INSUFFICIENT_FUNDS: 'insufficient funds',
  DO_NOT_HONOR: 'do not honor',
  DECLINED_CALL_ISSUER: 'declined - call issuer',
  TRANSACTION_NOT_ALLOWED: 'transaction not allowed',
  ISSUER_RESTRICTION: 'issuer or cardholder has put a restriction on the card',
  CLOSED_CARD: 'closed card',
} as const;

export const MAGENTO_EXPIRED_CARD_KEYWORDS = {
  EXPIRED_CARD: 'expired card',
} as const;

// Braintree 91565/91566. MUST stay ahead of MAGENTO_GENERIC_BILLING_ERROR_KEYWORDS, whose
// 'payment' keyword would otherwise swallow this into the opposite instruction.
export const MAGENTO_UNUSABLE_NONCE_KEYWORDS = {
  EXPIRED_PAYMENT_METHOD: 'payment method has expired',
  REUSED_NONCE: 'payment_method_nonce more than once',
  UNKNOWN_NONCE: 'unknown or expired payment_method_nonce',
} as const;

export const MAGENTO_CARD_NOT_ACTIVATED_KEYWORDS = {
  CARD_NOT_ACTIVATED: 'card not activated',
} as const;

export const MAGENTO_NO_ACCOUNT_KEYWORDS = {
  NO_SUCH_ISSUER: 'no such issuer',
  NO_ACCOUNT: 'no account',
} as const;

export const MAGENTO_CVV_DECLINED_KEYWORDS = {
  CVV: 'cvv',
} as const;

export const MAGENTO_PROCESSOR_DECLINED_KEYWORDS = {
  PROCESSOR_DECLINED: 'processor declined',
  FRAUD_SUSPECTED: 'fraud suspected',
  CANNOT_AUTHORIZE: 'cannot authorize at this time',
  CALL_ISSUER: 'call issuer. pick up card',
} as const;

export const MAGENTO_PAYPAL_ERROR_KEYWORDS = {
  PAYPAL_REVOCATION: 'paypal buyer',
  FUNDING_INSTRUMENT_DECLINED: 'funding instrument in the paypal',
} as const;

export const NON_CC = {
  paypal: 'PayPal',
  applepay: 'Apple Pay',
  venmo: 'Venmo',
  googlepay: 'Google Pay',
} as const;

export const SCROLL_TIME = 550;

export const SHIPPING_CODES = {
  EXPRESS: 'matrixrate_express',
  EXPEDITED: 'matrixrate_intermediate',
  STANDARD: 'matrixrate_standard',
} as const;

export const STEP = {
  ADDRESS: 1,
  VERIFICATION: 2,
  SHIP_METHOD: 3,
  BILLING: 4,
  REVIEW: 5,
} as const;
